<!--
====================================================================
Copyright 2024-2025 EcoNet.NZ

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
====================================================================
-->
<script setup>
import { ref } from 'vue';
import AlertBox from './AlertBox';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Select from 'primevue/select';
import Fieldset from 'primevue/fieldset';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import RadioButton from 'primevue/radiobutton';

const showNextVisitHelp = ref(false);
</script>

<template>
    <!-- Environment Warning Headers -->
    
    <!-- iNaturalist Production Warning - ONLY in DEV environment -->
    <div v-if="provider === 'inaturalist' && !isProduction" 
         class="production-warning-header p-3 mb-4 text-center">
      <h2 class="text-red-600 font-bold mb-2">🚨 PRODUCTION ENVIRONMENT 🚨</h2>
      <p class="text-red-700 font-medium">This application is running against PRODUCTION iNaturalist</p>
      <p class="text-red-600 text-sm">Changes made here WILL affect production data</p>
    </div>
    
    <!-- CAMS Development Environment Warning - ONLY in DEV environment -->
    <div v-if="provider === 'cams' && !isProduction" 
         class="test-warning-header p-3 mb-4 text-center">
      <h2 class="text-orange-600 font-bold mb-2">⚠️ TEST ENVIRONMENT ⚠️</h2>
      <p class="text-orange-700 font-medium">This application is running against a TEST clone of the weed layer</p>
      <p class="text-orange-600 text-sm">Changes made here will NOT affect production data</p>
    </div>
    
    <!-- PRODUCTION Environment: NO WARNING HEADERS -->
    <!-- When isProduction === true, no warning headers are displayed -->
    <!-- This applies to both CAMS and iNaturalist in production -->

    <Fieldset v-if="controlled" legend="Control Details" :toggleable="true" class="p-0 m-0 mb-3">
      <div class="fieldset-content">
        <div class="flex align-items-center gap-2 mb-3" :class="{ 'p-error': !fullyControlledValid }">
          <label class="font-medium text-900 w-6rem">Controlled?</label>
          <div class="flex flex-wrap gap-4 align-items-center">
            <div class="flex align-items-center">
              <RadioButton v-model="fullyControlled" value="fully" inputId="fully" name="fullyControlled" />
              <label for="fully" class="ml-2 font-medium text-900">Fully<span aria-hidden="true" class="ml-1 status-sym status-sym-yellow status-sym-diamond"></span></label>
            </div>
            <div v-if="provider === 'cams'" class="flex align-items-center">
              <RadioButton v-model="fullyControlled" value="partially-might-seed" inputId="partially-might-seed" name="fullyControlled" />
              <label for="partially-might-seed" class="ml-2 font-medium text-900">Partially - might seed this year<span aria-hidden="true" class="ml-1 status-sym status-sym-red status-sym-dot"></span></label>
            </div>
            <div v-if="provider === 'cams'" class="flex align-items-center">
              <RadioButton v-model="fullyControlled" value="partially-wont-seed" inputId="partially-wont-seed" name="fullyControlled" />
              <label for="partially-wont-seed" class="ml-2 font-medium text-900">Partially - won't seed this year<span aria-hidden="true" class="ml-1 status-sym status-sym-orange status-sym-dot"></span></label>
            </div>
            <div v-if="provider === 'inaturalist'" class="flex align-items-center">
              <RadioButton v-model="fullyControlled" value="partially" inputId="partially" name="fullyControlled" />
              <label for="partially" class="ml-2 font-medium text-900">Partially<span aria-hidden="true" class="ml-1 status-sym status-sym-red status-sym-dot"></span></label>
            </div>
            <span v-if="!fullyControlledValid" class="font-medium">Choose one of the options</span>
          </div>
        </div>

      <div class="flex align-items-center gap-2 mb-3" :class="{ 'p-error': !controlMethodValid }" >
        <label for="controlMethod" class="font-medium text-900 w-6rem" >Control Method</label>
        <Select v-model="controlMethod" :options="controlMethods" optionLabel="name" optionValue="code" class="p-2 border-1 border-300 border-round w-full" /> 
        <span v-if="!controlMethodValid">Control Method is required.</span>
      </div>

      <div class="flex align-items-center gap-2 mb-3" :class="{ 'p-error': !treatmentSubstanceValid }">
        <label for="treatmentSubstance" class="font-medium text-900 w-6rem" >Treatment Substance</label>
        <Select v-model="treatmentSubstance" :options="treatmentSubstances" optionLabel="name" optionValue="code" class="p-2 border-1 border-300 border-round w-full" />
        <span v-if="!treatmentSubstanceValid">Treatment Substance is required.</span>
      </div>

      <div class="flex align-items-center gap-2 mb-3">
        <label for="treatmentDetails" class="font-medium text-900 w-6rem">Treatment Details</label>
        <InputText type="text" id="treatmentDetails" v-model="treatmentDetails" :maxlength="fieldLimits.treatmentDetails" placeholder="Enter any additional details of treatment, e.g. herbicide mix " class="p-3 border-1 border-300 border-round w-full"/>
      </div>

      <!-- Pods removed field hidden for iNaturalist -->
      <div v-if="provider === 'cams'" class="flex align-items-center gap-2 mb-3">
        <label for="podsRemoved" class="font-medium text-900 w-10rem"># pods, seed heads etc removed</label>
        <div class="pods-input-wrapper">
          <InputNumber id="podsRemoved" v-model="podsRemoved" showButtons fluid :max="9999" :step="1" :min="0"/>
        </div>
      </div>
      </div>
    </Fieldset>

    <!-- Alive status details - Only show for CAMS -->
    <Fieldset v-if="alive && provider === 'cams'" legend="Status Details" :toggleable="true" class="p-0 m-0 mb-3">
      <div class="fieldset-content">
        <div class="flex align-items-center gap-2 mb-3" :class="{ 'p-error': !aliveStatusValid }">
          <label class="font-medium text-900 w-6rem">Status</label>
          <div class="flex flex-wrap gap-4 align-items-center">
            <div class="flex align-items-center">
              <RadioButton v-model="aliveStatus" value="alive-might-seed" inputId="alive-might-seed" name="aliveStatus" />
              <label for="alive-might-seed" class="ml-2 font-medium text-900">Might seed this year<span aria-hidden="true" class="ml-1 status-sym status-sym-red status-sym-dot"></span></label>
            </div>
            <div class="flex align-items-center">
              <RadioButton v-model="aliveStatus" value="alive-wont-seed" inputId="alive-wont-seed" name="aliveStatus" />
              <label for="alive-wont-seed" class="ml-2 font-medium text-900">Won't seed this year<span aria-hidden="true" class="ml-1 status-sym status-sym-orange status-sym-dot"></span></label>
            </div>
            <div class="flex align-items-center">
              <RadioButton v-model="aliveStatus" value="alive-occupant-will-control" inputId="alive-occupant-will-control" name="aliveStatus" />
              <label for="alive-occupant-will-control" class="ml-2 font-medium text-900">Occupant/owner will control<span aria-hidden="true" class="ml-1 status-sym status-sym-pink status-sym-diamond"></span></label>
            </div>
            <span v-if="!aliveStatusValid" class="font-medium">Choose one of the options</span>
          </div>
        </div>
      </div>
    </Fieldset>

    <Fieldset v-if="controlled || alive" legend="Weed Details" :toggleable="true" class="p-0 m-0">
      <div class="fieldset-content">
        <div class="flex align-items-center gap-2 mb-3">
        <label for="locationDetails" class="font-medium text-900 w-6rem">{{ provider === 'cams' ? 'Location Address' : 'Location Details' }}</label>
        <InputText type="text" id="locationDetails" placeholder="Street address or reserve name or similar" v-model="locationDetails" class="p-3 border-1 border-300 border-round w-full"/>
      </div>
      <div v-if="provider === 'cams'" class="flex align-items-center gap-2 mb-3">
        <label for="locationDescription" class="font-medium text-900 w-6rem">Location Notes</label>
        <InputText type="text" id="locationDescription" placeholder="Where to find the weed (e.g. back fence, left of steps going down, etc.)" v-model="locationDescription" :maxlength="fieldLimits.locationDescription" class="p-3 border-1 border-300 border-round w-full"/>
      </div>

      <!-- Area field - iNaturalist interface using CAMS-style widget -->
      <div v-if="(controlled || alive) && provider === 'inaturalist'" class="flex align-items-center gap-2 mb-3">
        <label for="area" class="font-medium text-900 w-6rem">Area</label>
        <div class="table-input-wrapper">
          <InputNumber id="area" v-model="area" showButtons fluid suffix=" m²" :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
        </div>
      </div>

      <!-- Height field - iNaturalist interface using CAMS-style widget -->
      <div v-if="(controlled || alive) && provider === 'inaturalist'" class="flex align-items-center gap-2 mb-3">
        <label for="height" class="font-medium text-900 w-6rem">Height</label>
        <div class="table-input-wrapper">
          <InputNumber id="height" v-model="height" showButtons fluid suffix=" m" :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
        </div>
      </div>

      <div class="flex align-items-center gap-2 mb-3" >
        <label for="phenology" class="font-medium text-900 w-6rem" >Growth Stage</label>
        <Select v-model="phenology" :options="phenologies" optionLabel="name" optionValue="code" class="p-2 border-1 border-300 border-round w-full" />
      </div>

      <div class="flex align-items-center gap-2 mb-3" >
        <label for="siteDifficulty" class="font-medium text-900 w-6rem" >Site Difficulty</label>
        <Select v-model="siteDifficulty" :options="siteDifficulties" optionLabel="name" optionValue="code" class="p-2 border-1 border-300 border-round w-full" />
      </div>


      <!-- Number inputs for Alive status - Only shown when Alive and using CAMS -->
      <div v-if="alive && provider === 'cams'" class="alive-inputs-container mt-4">
        <div class="flex align-items-center gap-2 mb-3">
          <label for="areaRemaining" class="font-medium text-900 w-6rem">Area</label>
          <div class="table-input-wrapper">
            <InputNumber id="areaRemaining" v-model="areaRemaining" showButtons fluid suffix=" m²" :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
          </div>
        </div>
        
        <div class="flex align-items-center gap-2 mb-3">
          <label for="heightRemaining" class="font-medium text-900 w-6rem">Height</label>
          <div class="table-input-wrapper">
            <InputNumber id="heightRemaining" v-model="heightRemaining" showButtons fluid suffix=" m" :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
          </div>
        </div>
        
        <div class="flex align-items-center gap-2 mb-3">
          <label for="plantsRemainingMature" class="font-medium text-900 w-6rem"># Mature plants</label>
          <div class="table-input-wrapper">
            <InputNumber id="plantsRemainingMature" v-model="plantsRemainingMature" showButtons fluid :step="1" :min="0"/>
          </div>
        </div>
        
        <div class="flex align-items-center gap-2 mb-3">
          <label for="plantsRemainingImmature" class="font-medium text-900 w-6rem"># Juvenile plants</label>
          <div class="table-input-wrapper">
            <InputNumber id="plantsRemainingImmature" v-model="plantsRemainingImmature" showButtons fluid :step="1" :min="0"/>
          </div>
        </div>
        
        <div class="flex align-items-center gap-2 mb-3">
          <label for="plantsRemainingSeedlings" class="font-medium text-900 w-6rem"># Seedlings</label>
          <div class="table-input-wrapper">
            <InputNumber id="plantsRemainingSeedlings" v-model="plantsRemainingSeedlings" showButtons fluid :step="1" :min="0"/>
          </div>
        </div>
      </div>

      <!-- Controlled/Remaining Comparison Table - Only shown when Controlled and using CAMS -->
      <div v-if="controlled && provider === 'cams'" class="comparison-table-container mt-4">
        <table class="comparison-table w-full">
          <thead>
            <tr>
              <th class="text-left p-3 font-medium text-900 border-bottom-1 border-300"></th>
              <th class="text-center p-3 font-bold text-900 border-bottom-3 border-500">Controlled</th>
              <th v-if="fullyControlled && fullyControlled !== 'fully'" class="text-center p-3 font-medium text-900 border-bottom-3 border-500">Remaining</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-bottom-1 border-200">
              <td class="p-3 font-medium text-900">Area (m²)</td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="areaControlled" showButtons fluid :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
                </div>
              </td>
              <td v-if="fullyControlled && fullyControlled !== 'fully'" class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="areaRemaining" showButtons fluid :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
                </div>
              </td>
            </tr>
            <tr v-if="fullyControlled && fullyControlled !== 'fully'" class="border-bottom-1 border-200">
              <td class="p-3 font-medium text-900">Height (m)</td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper" style="visibility: hidden;">
                  <InputNumber v-model="heightControlled" showButtons fluid :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
                </div>
              </td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="heightRemaining" showButtons fluid :step="1" :min="0" :minFractionDigits="0" :maxFractionDigits="1"/>
                </div>
              </td>
            </tr>
            <tr class="border-bottom-1 border-200">
              <td class="p-3 font-medium text-900"># Mature plants</td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsControlledMature" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
              <td v-if="fullyControlled && fullyControlled !== 'fully'" class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsRemainingMature" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
            </tr>
            <tr class="border-bottom-1 border-200">
              <td class="p-3 font-medium text-900"># Juvenile plants</td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsControlledImmature" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
              <td v-if="fullyControlled && fullyControlled !== 'fully'" class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsRemainingImmature" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
            </tr>
            <tr class="border-bottom-1 border-200">
              <td class="p-3 font-medium text-900"># Seedlings</td>
              <td class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsControlledSeedlings" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
              <td v-if="fullyControlled && fullyControlled !== 'fully'" class="p-2 text-center">
                <div class="table-input-wrapper">
                  <InputNumber v-model="plantsRemainingSeedlings" showButtons fluid :step="1" :min="0"/>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </Fieldset>

    <!-- Dead status details - Only show radio buttons for CAMS -->
    <Fieldset v-if="dead && provider === 'cams'" legend="Status Details" :toggleable="true" class="p-0 m-0">
      <div class="fieldset-content">
        <div class="flex align-items-center gap-2 mb-3">
          <label class="font-medium text-900 w-6rem">Status</label>
          <div class="flex gap-4">
            <div class="flex align-items-center">
              <RadioButton v-model="deadStatus" value="found-confirmed-dead" inputId="found-confirmed-dead" name="deadStatus" />
              <label for="found-confirmed-dead" class="ml-2 font-medium text-900">Found - confirmed dead</label>
            </div>
            <div class="flex align-items-center">
              <RadioButton v-model="deadStatus" value="not-found" inputId="not-found" name="deadStatus" />
              <label for="not-found" class="ml-2 font-medium text-900">Not found</label>
            </div>
          </div>
        </div>
      </div>
    </Fieldset>

    <Fieldset v-if="alive" legend="Person Hours" :toggleable="true" class="p-0 m-0 mt-3">
      <div class="fieldset-content">
        <!-- Person Hours - Using Calendar for better duration input -->
      <div class="flex flex-column gap-3">
        <div class="flex align-items-center gap-2">
          <label class="font-medium text-900 w-6rem">Remaining</label>
          <div class="flex align-items-center gap-2">
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="remainingHours" 
                showButtons
                fluid
                :min="0" 
                :max="999" 
                :step="1" 
                @input="updateRemainingFromManual"
                @focus="initializeDurationField('remainingHours')"
              />
            </div>
            <span class="duration-unit">hr</span>
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="remainingMins" 
                showButtons
                fluid
                :min="0" 
                :max="60" 
                :step="6" 
                @input="validateAndUpdateRemainingMins"
                @focus="initializeDurationField('remainingMins')"
              />
            </div>
            <span class="duration-unit">min</span>
          </div>
        </div>
      </div>
      </div>
    </Fieldset>

    <!-- Person Hours -->
    <Fieldset v-if="controlled && (provider === 'cams' || fullyControlled === 'partially' || fullyControlled === 'partially-might-seed' || fullyControlled === 'partially-wont-seed')" legend="Person Hours" :toggleable="true" class="p-0 m-0 mt-3">
      <div class="fieldset-content">
        <!-- Person Hours - Using Calendar for better duration input -->
      <div class="flex flex-column gap-3">
        <!-- This visit - Only show for CAMS -->
        <div v-if="provider === 'cams'" class="flex align-items-center gap-2">
          <label class="font-medium text-900 w-6rem">This visit</label>
          <div class="flex align-items-center gap-2">
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="thisVisitHours" 
                :min="0" 
                :max="999" 
                :step="1" 
                showButtons
                fluid
                @input="updateThisVisitFromManual"
                @focus="initializeDurationField('thisVisitHours')"
              />
            </div>
            <span class="duration-unit">hr</span>
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="thisVisitMins" 
                :min="0" 
                :max="60" 
                :step="6" 
                showButtons
                fluid
                @input="validateAndUpdateThisVisitMins"
                @focus="initializeDurationField('thisVisitMins')"
              />
            </div>
            <span class="duration-unit">min</span>
          </div>
        </div>
        <!-- Remaining - Show for both CAMS and iNaturalist when partially controlled -->
        <div v-if="fullyControlled === 'partially' || fullyControlled === 'partially-might-seed' || fullyControlled === 'partially-wont-seed'" class="flex align-items-center gap-2">
          <label class="font-medium text-900 w-6rem">Remaining</label>
          <div class="flex align-items-center gap-2">
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="remainingHours" 
                showButtons
                fluid
                :min="0" 
                :max="999" 
                :step="1" 
                @input="updateRemainingFromManual"
                @focus="initializeDurationField('remainingHours')"
              />
            </div>
            <span class="duration-unit">hr</span>
            <div class="duration-input-wrapper">
              <InputNumber 
                v-model="remainingMins" 
                showButtons
                fluid
                :min="0" 
                :max="60" 
                :step="6" 
                @input="validateAndUpdateRemainingMins"
                @focus="initializeDurationField('remainingMins')"
              />
            </div>
            <span class="duration-unit">min</span>
          </div>
        </div>
      </div>
      </div>
    </Fieldset>

    <!-- Communication Made - Only show for CAMS -->
    <div v-if="(controlled || alive) && provider === 'cams'" class="flex align-items-center gap-2 mb-3 mt-3">
      <label for="communicationMade" class="font-medium text-900 w-6rem">Communication Made</label>
      <Select v-model="communicationMade" :options="communicationMadeOptions" optionLabel="name" optionValue="code" class="p-2 border-1 border-300 border-round w-full" />
    </div>

    <!-- Additional Notes - Only show for CAMS -->
    <div v-if="(controlled || alive) && provider === 'cams'" class="flex align-items-center gap-2 mb-3 mt-3">
      <label for="notes" class="font-medium text-900 w-6rem">Additional Notes</label>
      <InputText type="text" id="notes" v-model="notes" :maxlength="fieldLimits.notes" placeholder="Enter any additional notes about this visit" class="p-2 border-1 border-300 border-round w-full"/>
    </div>

    <!-- Date for next visit. Hidden for Duplicate. -->
    <div v-if="showDateForNextVisit" class="mb-3 mt-3" :class="{ 'p-error': !nextVisitDateValid }">
      <div class="flex align-items-center gap-2">
        <label for="date-for-next-visit" class="font-medium text-900 w-6rem">Date for next visit</label>
        <div class="next-visit-date-field">
          <DatePicker id="date-for-next-visit" v-model="dateForNextVisit" dateFormat="dd/mm/yy" :minDate="tomorrow" :maxDate="nextVisitMaxDate" @show="showNextVisitMonth" @update:model-value="onNextVisitDateInput" />
          <Button
            type="button"
            icon="pi pi-info-circle"
            text
            rounded
            class="next-visit-help-btn"
            aria-label="Guidelines for the next visit date"
            :aria-expanded="showNextVisitHelp"
            aria-controls="next-visit-help"
            @click="showNextVisitHelp = !showNextVisitHelp"
          />
        </div>
      </div>
      <span v-if="nextVisitDateRequired && !dateForNextVisit">Date for next visit is required.</span>
      <span v-else-if="dateForNextVisit && !nextVisitDateWithinMax">Date for next visit must be within 5 years.</span>
      <div v-if="showNextVisitHelp" id="next-visit-help" class="next-visit-help">
        <p>On this date the dot is flagged for a revisit, shown with a purple hexagon border.</p>
        <p v-if="nextVisitHelp.lead">{{ nextVisitHelp.lead }}</p>
        <ul v-if="nextVisitHelp.items.length">
          <li v-for="item in nextVisitHelp.items" :key="item">{{ item }}</li>
        </ul>
      </div>
    </div>

    <!-- Notes for dead/duplicate - Only show for CAMS -->
    <div v-if="(dead || duplicate) && provider === 'cams'" class="flex align-items-center gap-2 mb-3 mt-3">
      <label for="notes-dead-duplicate" class="font-medium text-900 w-6rem">Notes</label>
      <InputText type="text" id="notes-dead-duplicate" v-model="notes" :maxlength="fieldLimits.notes" placeholder="Enter any notes about this status update" class="p-2 border-1 border-300 border-round w-full"/>
    </div>

    <ProgressSpinner v-if="isLoading" />

    <div class="flex flex-column gap-3">
      <Button @click="updateWeedStatus" label="Update Weed Status" :disabled="isButtonDisabled" class="update-weed-status-btn p-3 border-1 border-300 border-round text-white font-medium" />
    </div>
    
    <div v-if="message" class="flex flex-column gap-3">
      <AlertBox :type="result">{{ message }}</AlertBox>
    </div>
</template>

<script>
const dayjs = require('dayjs')
const { suggestNextVisitDate, maxNextVisitDate } = require('../nextVisitDate')
const { FIELD_LIMITS, limitText } = require('../fieldLimits')

// iNaturalist constants - commented out for future use
// const OBSERVATION_FIELD_ID = {
//   treated: 15709,
//   dateControlled: 6508,
//   howTreated: 15710,
//   treatmentSubstance: 15662,
//   treatmentDetails: 15712,
//   locationDetails: 5453,
//   area: 12414,
//   height: 587,
//   phenology: 1759,
//   siteDifficulty: 15708,
//   effort: 15661,
//   statusUpdate: 15795,
//   dateOfStatusUpdate: 15796,
//   followUpDate: 14309,    // deprecated
//   dateForNextVisit: 18871
// }
// const ALIVE_FIELD_VALUE = 'Alive / Regrowth'
// const DEAD_FIELD_VALUE = 'Dead / Not Present' 
// const DUPLICATE_FIELD_VALUE = 'Duplicate' 

export default {
  name: 'EasyEditor',
  props: {
    code: String,        // Legacy auth-code support
    sessionId: String,   // New session-based auth
    observationId: String,  // Keep for potential future iNaturalist integration
    dateControlled: String,
    dateOfStatusUpdate: String,
    controlled: Boolean,
    alive: Boolean,
    dead: Boolean,
    duplicate: Boolean,
    cachedVisitData: Object,  // Cached data from parent
    provider: String,    // Data provider: 'cams' or 'inaturalist'
    environment: String, // 'dev' | 'prod'
    isProduction: Boolean, // true if production environment
    visitDate: Date      // Visit date with time from parent App.vue
  },

  emits: ['updateRequested'],

  data() {
    return {
      message: '',
      result: '',
      fieldLimits: FIELD_LIMITS,

      fullyControlled: null,
      initialTreated: '',

      controlMethod: '',
      treatmentSubstance: '',
      treatmentDetails: '',
      initialControlMethod: '',
      initialTreatmentSubstance: '',
      initialTreatmentDetails: '',

      locationDetails: '',
      locationDescription: '',
      area: '',  // For iNaturalist interface only
      height: '',
      phenology: '',
      siteDifficulty: '',
      initialLocationDetails: '',
      initialArea: '',  // For iNaturalist interface only
      initialHeight: '',
      initialPhenology: '',
      initialSiteDifficulty: '',
      
      dateForNextVisit: null,
      initialDateForNextVisit: null,
      nextVisitOverridden: false,
      applyingNextVisitSuggestion: false,
      lastSuggestedNextVisit: null,

      // Control Details
      podsRemoved: 0,

      // Before/After comparison data
      areaControlled: 0,
      areaRemaining: 0,
      heightControlled: 0,
      heightRemaining: 0,
      plantsControlledMature: 0,
      plantsRemainingMature: 0,
      plantsControlledImmature: 0,
      plantsRemainingImmature: 0,
      plantsControlledSeedlings: 0,
      plantsRemainingSeedlings: 0,
      beforeEffortRequired: '',
      afterEffortRequired: '',

      // Dead status details
      deadStatus: '',

      // Alive status details (CAMS: no default; user must choose)
      aliveStatus: null,

      // Notes field for all screens
      notes: '',
      initialNotes: '',

      // Communication Made field
      communicationMade: 'None',

      // This Visit/Remaining data (duration format - hours and minutes)
      thisVisitHours: 0,
      thisVisitMins: 0,
      remainingHours: 0,
      remainingMins: 0,
      
      // Calendar-based duration inputs (Date objects for time-only input) - initialized to 00:00
      thisVisitDuration: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),
      remainingDuration: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),

      isLoading: false,

      today: new Date(),

      controlMethods: [
        { name: 'Cut and paste',        code: 'Cut and paint' },
        { name: 'Drill and fill',       code: 'Drill and fill' },
        { name: 'Scrape and paste',     code: 'Scrape stem and paste' },
        { name: 'Pulled or dug',        code: 'Pulled or dug' },
        { name: 'Ringbark',             code: 'Ringbark' },
        { name: 'Spray leaves',         code: 'Spray leaves' },
        { name: 'Cut but roots remain', code: 'Cut but roots remain' },
        { name: "Don't know",           code: "Don't know" },
      ],
      treatmentSubstances: [
        { name: 'None',                        code: 'None' },
        { name: 'CutNPaste Original Gel',      code: 'CutNPaste Original Gel' },
        { name: 'CutNPaste Bamboo Buster Gel', code: 'CutNPaste Bamboo Buster Gel' },
        { name: 'CutNPaste Glimax',            code: 'CutNPaste Glimax' },
        { name: 'CutNPaste MetGel',            code: 'CutNPaste MetGel' },
        { name: 'CutNPaste Picloram Gel',      code: 'CutNPaste Picloram Gel' },
        { name: 'Dow Vigilant II',             code: 'Dow Vigilant II' },
        { name: "Triumph Gel / Vigilant",      code: "Triumph Gel / Vigilant" },        
        { name: 'Weed Weapon Invade Gel',      code: 'Weed Weapon Invade Gel' },
        { name: 'Weed Weapon Stump-Stop-Gel',  code: 'Weed Weapon Stump-Stop-Gel' },
        { name: 'OTHER GEL',                   code: 'OTHER GEL' },
        { name: 'Glyphosate Spray',            code: 'Glyphosate Spray' },
        { name: 'Haloxyfop Spray',             code: 'Haloxyfop Spray' },
        { name: 'MetSulf Spray',               code: 'MetSulf Spray' },
        { name: 'Triclopyr Spray',             code: 'Triclopyr Spray' },
        { name: 'OTHER LIQUID Herbicide',      code: 'OTHER LIQUID Herbicide' },
        { name: 'Glyphosate Granules',         code: 'Glyphosate Granules' },
        { name: 'Other',                       code: 'OTHER-TREATMENT' },
      ],
      phenologies: [
        // { name: 'Not recorded',         code: 'not recorded' },
        { name: 'Vegetative only',      code: 'vegetative only' },
        { name: 'Flower buds',          code: 'flower buds' },
        { name: 'Flowers',              code: 'flowers' },
        { name: 'Immature fruit',       code: 'immature fruit' },
        { name: 'Mature fruit',         code: 'mature fruit' },
        { name: 'Seed dispersed',       code: 'seed dispersed' },
      ],
      /*
       * Site Difficulty Uses Standardized Numeric Codes
       * ===============================================
       * 
       * The UI uses simple numeric codes (1-5) for Site Difficulty
       * to maintain consistency and compatibility between different backend systems
       * (CAMS and iNaturalist). The API layer handles translation between these
       * standardized codes and the specific format required by each backend system.
       * 
       * This approach provides:
       * - Consistent interface values regardless of backend
       * - Easier maintenance and debugging
       * - Flexibility to support different backend systems
       * - Clear separation of UI concerns from backend data formats
       */
      siteDifficulties: [
        { name: 'Easy (for most people)',                         code: 1 },
        { name: 'Moderate',                                       code: 2 },
        { name: 'Challenging (may be rough or steep)',            code: 3 },
        { name: 'Advanced (beyond most abilities)',               code: 4 },
        { name: 'Professional skills required (eg rope access)',  code: 5 },
      ],
      communicationMadeOptions: [
        { name: 'None',                               code: 'None' },
        { name: 'Left flyer',                         code: 'Left flyer' },
        { name: 'Talked to occupant; gave flyer',     code: 'Talked to occupant; gave flyer' },
      ]
    };
  },

  computed: {
    controlMethodValid() {
      return this.controlMethod && this.controlMethod.trim() !== '';
    },
    treatmentSubstanceValid() {
      return this.treatmentSubstance && this.treatmentSubstance.trim() !== '';
    },
    fullyControlledValid() {
      if (!this.controlled) return true;
      const v = this.fullyControlled;
      return (
        v === 'fully' ||
        v === 'partially' ||
        v === 'partially-might-seed' ||
        v === 'partially-wont-seed'
      );
    },
    aliveStatusValid() {
      if (!(this.alive && this.provider === 'cams')) return true;
      const v = this.aliveStatus;
      return (
        v === 'alive-might-seed' ||
        v === 'alive-wont-seed' ||
        v === 'alive-occupant-will-control'
      );
    },
    visitDateValid() {
      if (!this.visitDate) {
        return false;
      }
      const date = new Date(this.visitDate);
      return !isNaN(date.getTime());
    },
    allFieldsValid() {
      if (!this.visitDateValid) {
        return false
      }
      if (this.controlled && !this.dateControlled) {
        return false
      }
      if ((this.alive || this.dead || this.duplicate) && !this.dateOfStatusUpdate) {
        return false
      }
      if (!this.nextVisitDateValid) {
        return false
      }
      if (this.controlled) {
        return (
          this.fullyControlledValid &&
          this.controlMethodValid &&
          this.treatmentSubstanceValid
        );
      }
      if (this.alive && this.provider === 'cams') {
        return this.aliveStatusValid;
      }
      return true
    },
    isButtonDisabled() {
      return this.isLoading || !this.allFieldsValid
    },
    tomorrow() {
      return dayjs().add(1, 'day').startOf('day').toDate();  // Get tomorrow's date at midnight
    },
    nextVisitMaxDate() {
      return maxNextVisitDate(new Date())
    },
    nextVisitDateRequired() {
      if (!this.showDateForNextVisit) return false
      return suggestNextVisitDate(this.visitDate, this.visitColour, this.speciesName) != null
    },
    nextVisitDateWithinMax() {
      if (!this.dateForNextVisit) return true
      const date = new Date(this.dateForNextVisit)
      if (Number.isNaN(date.getTime())) return false
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      return day <= this.nextVisitMaxDate
    },
    nextVisitDateValid() {
      if (this.showDateForNextVisit && this.dateForNextVisit && !this.nextVisitDateWithinMax) return false
      if (!this.nextVisitDateRequired) return true
      return !!this.dateForNextVisit
    },
    formattedDateForNextVisit() {
      if (this.dateForNextVisit) {
        return dayjs(this.dateForNextVisit).format('YYYY-MM-DDTHH:mm:ssZ');
      }
      return null
    },
    speciesName() {
      const data = this.cachedVisitData
      if (!data || data.species_name == null) return ''
      return String(data.species_name)
    },
    visitColour() {
      if (this.duplicate) return 'duplicate'
      if (this.controlled) {
        if (this.fullyControlled === 'fully') return 'yellow'
        if (this.fullyControlled === 'partially-wont-seed') return 'orange'
        if (this.fullyControlled === 'partially-might-seed' || this.fullyControlled === 'partially') return 'red'
        return null
      }
      if (this.alive) {
        if (this.provider !== 'cams') return 'red'
        if (this.aliveStatus === 'alive-wont-seed') return 'orange'
        if (this.aliveStatus === 'alive-might-seed') return 'red'
        if (this.aliveStatus === 'alive-occupant-will-control') return 'pink'
        return null
      }
      if (this.dead) return 'green'
      return null
    },
    showDateForNextVisit() {
      return this.controlled || this.alive || this.dead
    },
    currentStatus() {
      if (this.controlled) return 'controlled'
      if (this.alive) return 'alive'
      if (this.dead) return 'dead'
      if (this.duplicate) return 'duplicate'
      return null
    },
    
    // Calculate DifficultyChild based on PersonHours Remaining
    calculatedDifficultyChild() {
      // Convert to numbers to ensure proper mathematical operations
      const hours = Number(this.remainingHours) || 0
      const mins = Number(this.remainingMins) || 0
      const totalHours = hours + (mins / 60.0)
      
      if (totalHours > 20) return 5
      if (totalHours > 8) return 4
      if (totalHours > 4) return 3
      if (totalHours > 1) return 2
      return 1
    },
    nextVisitHelp() {
      const partial = this.fullyControlled === 'partially-might-seed'
        || this.fullyControlled === 'partially-wont-seed'
        || this.fullyControlled === 'partially'
      if (this.dead) {
        return {
          lead: 'Seed may still be in the soil, so seedlings might grow. Set the date for when they are likely to mature. That depends on the species.',
          items: []
        }
      }
      if (this.alive) {
        return {
          lead: 'Set the date for when this site should be checked again.',
          items: []
        }
      }
      if (this.controlled && this.fullyControlled === 'fully') {
        return {
          lead: 'Unsure every plant was killed: set a date 1–3 months ahead.',
          items: []
        }
      }
      if (this.controlled && partial) {
        return {
          lead: 'Set the date for when this site should be checked again.',
          items: [
            'Unsure every plant was killed: set a date 1–3 months ahead.',
            'Mature plants killed, and seedlings or juvenile plants remain: set the date to the spring when they are expected to flower (for example, 1 October next year).'
          ]
        }
      }
      return { lead: '', items: [] }
    }
  },

  watch: {
    // Auto-set Treatment Substance to "None" when Control Method is "Pulled or dug"
    controlMethod(newVal) {
      if (newVal === 'Pulled or dug') {
        this.treatmentSubstance = 'None'
      }
    },
    fullyControlled() {
      this.applyNextVisitSuggestion()
    },
    aliveStatus() {
      this.applyNextVisitSuggestion()
    },
    visitDate() {
      this.applyNextVisitSuggestion()
    },
    // Handle Dead status button changes - populate/clear Notes field
    deadStatus(newVal) {
      if (this.dead) {
        const buttonText = newVal === 'found-confirmed-dead' ? 'Found - confirmed dead' : 'Not found'
        this.notes = buttonText
      }
    },
    // Watch for manual changes to Notes field for Dead status
    notes(newVal) {
      if (this.dead) {
        // If notes are cleared or don't match button text, deselect the button
        const foundText = 'Found - confirmed dead'
        const notFoundText = 'Not found'
        
        if (newVal !== foundText && newVal !== notFoundText) {
          // If text doesn't match either button, keep current selection but allow custom text
          // The button state will remain as is when text is appended/modified
        } else if (newVal === foundText) {
          this.deadStatus = 'found-confirmed-dead'
        } else if (newVal === notFoundText) {
          this.deadStatus = 'not-found'
        }
        
        // If notes are completely cleared, deselect button
        if (!newVal || newVal.trim() === '') {
          // Reset to default but don't clear the field - let user choose
        }
      }
    }
  },

  created: async function() {
    console.log('=== EasyEditor created() lifecycle hook called ===')
    console.log('Props received:', {
      code: this.code,
      sessionId: this.sessionId,
      observationId: this.observationId,
      controlled: this.controlled,
      alive: this.alive,
      dead: this.dead,
      duplicate: this.duplicate,
      cachedVisitData: this.cachedVisitData
    })
    
    // Use cached data from parent instead of loading it ourselves
    console.log('Using cached visit data from parent:', this.cachedVisitData)
    
    // Populate fields immediately with cached data
    this.populateFieldsFromCachedData()
    this.applyNextVisitSuggestion()

    console.log('EasyEditor initialized with status:', this.currentStatus)
  },

  methods: {
    applyNextVisitSuggestion() {
      if (this.nextVisitOverridden || !this.showDateForNextVisit) return
      const suggested = suggestNextVisitDate(
        this.visitDate,
        this.visitColour,
        this.speciesName
      )
      this.applyingNextVisitSuggestion = true
      this.lastSuggestedNextVisit = suggested
      this.dateForNextVisit = suggested
      this.$nextTick(() => {
        this.applyingNextVisitSuggestion = false
        this.showNextVisitMonth()
      })
    },

    // PrimeVue chooses the popup month before it stores the model, so a
    // suggested date would open on today. Copying the date afterwards
    // makes the popup use the date already in the box.
    showNextVisitMonth() {
      const current = this.dateForNextVisit
      if (!(current instanceof Date) || Number.isNaN(current.getTime())) return
      this.dateForNextVisit = new Date(current.getFullYear(), current.getMonth(), current.getDate())
    },

    onNextVisitDateInput(value) {
      if (this.applyingNextVisitSuggestion) return
      if (this.sameCalendarDay(value, this.lastSuggestedNextVisit)) return
      this.nextVisitOverridden = true
    },

    sameCalendarDay(a, b) {
      if (!a && !b) return true
      if (!a || !b) return false
      const left = new Date(a)
      const right = new Date(b)
      if (Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) return false
      return left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate()
    },

    populateFieldsFromCachedData() {
      if (!this.cachedVisitData) {
        return
      }
      
      const data = this.cachedVisitData
      
      // Populate fields from cached data
      if (data.locationDetails) {
        this.locationDetails = data.locationDetails
      }
      if (data.locationDescription) {
        this.locationDescription = limitText(data.locationDescription, FIELD_LIMITS.locationDescription)
      }
      
      if (data.area) {
        this.area = data.area
      }
      if (data.height) {
        this.height = data.height
      }
      if (data.siteDifficulty) {
        this.siteDifficulty = data.siteDifficulty
      }
      
      if (data.controlMethod) {
        this.controlMethod = data.controlMethod
      }
      if (data.treatmentSubstance) {
        this.treatmentSubstance = data.treatmentSubstance
      }
      if (data.treatmentDetails) {
        this.treatmentDetails = limitText(data.treatmentDetails, FIELD_LIMITS.treatmentDetails)
      }
      
      // Notes field intentionally not populated from cached data - always starts blank
      
      // Note: Remaining/Controlled values and Person Hours fields are intentionally 
      // kept at their default value of 0, even if cached data is available
    },

    // iNaturalist helper method - commented out for future use
    // getFieldValue(ofvs, fieldName) {
    //   var arr = ofvs.filter(ofv => ofv.field_id === OBSERVATION_FIELD_ID[fieldName])
    //   if (arr.length === 0)
    //     return null
    //   return arr[0].value
    // },
    
    createJsonBody() {
      // Send standardized values to API for backend system compatibility
      // The API handles translation from these standard codes to system-specific formats
      return JSON.stringify({
        status: this.currentStatus,
        dateControlled: this.dateControlled,
        dateOfStatusUpdate: this.dateOfStatusUpdate,
        visitDate: this.visitDate ? dayjs(this.visitDate).format('YYYY-MM-DDTHH:mm:ssZ') : null,
        // Additional fields ready for future implementation:
        fullyControlled: this.fullyControlled,
        controlMethod: this.controlMethod,
        treatmentSubstance: this.treatmentSubstance,
        treatmentDetails: limitText(this.treatmentDetails, FIELD_LIMITS.treatmentDetails),
        locationDetails: this.locationDetails,
        locationDescription:
          this.provider === 'cams' && (this.controlled || this.alive)
            ? limitText(this.locationDescription, FIELD_LIMITS.locationDescription)
            : undefined,
        area: this.provider === 'inaturalist' ? this.area : undefined,  // Only for iNaturalist
        height: this.provider === 'inaturalist' ? this.height : undefined,  // Only for iNaturalist
        phenology: this.phenology,
        siteDifficulty: this.siteDifficulty,
        calculatedDifficultyChild: this.calculatedDifficultyChild,
        dateForNextVisit: this.showDateForNextVisit ? this.formattedDateForNextVisit : undefined,
        
        // New fields for Before/After comparison
        podsRemoved: this.podsRemoved,
        areaControlled: this.areaControlled,
        areaRemaining: this.areaRemaining,
        heightRemaining: this.heightRemaining,
        plantsControlledMature: this.plantsControlledMature,
        plantsRemainingMature: this.plantsRemainingMature,
        plantsControlledImmature: this.plantsControlledImmature,
        plantsRemainingImmature: this.plantsRemainingImmature,
        plantsControlledSeedlings: this.plantsControlledSeedlings,
        plantsRemainingSeedlings: this.plantsRemainingSeedlings,
        beforeEffortRequired: this.beforeEffortRequired,
        afterEffortRequired: this.afterEffortRequired,
        
        // Dead status details
        deadStatus: this.deadStatus,
        
        // Alive status details (API: occupant flag + seed timing code)
        aliveStatus:
          this.aliveStatus === 'alive-occupant-will-control'
            ? 'alive-might-seed'
            : this.aliveStatus,
        occupantWillControl: this.aliveStatus === 'alive-occupant-will-control',
        
        // Notes field for all screens
        notes: limitText(this.notes, FIELD_LIMITS.notes),
        
        // Communication Made field (only include if not "None")
        communicationMade: this.communicationMade !== 'None' ? this.communicationMade : undefined,
        
        // Person hours data (convert to total minutes for storage)
        thisVisitHours: this.thisVisitHours,
        thisVisitMins: this.thisVisitMins,
        remainingHours: this.remainingHours,
        remainingMins: this.remainingMins
      })
    },

    // iNaturalist JSON body creation - commented out for future use
    // createiNaturalistJsonBody() {
    //   var fields = {observationId: this.observationId}

    //   if (this.controlled || this.alive) {
    //     if (this.locationDetails    != this.initialLocationDetails)     fields[OBSERVATION_FIELD_ID['locationDetails']] = this.locationDetails
    //     if (this.area               != this.initialArea)                fields[OBSERVATION_FIELD_ID['area']] = this.area
    //     if (this.height             != this.initialHeight)              fields[OBSERVATION_FIELD_ID['height']] = this.height
    //     if (this.siteDifficulty     != this.initialSiteDifficulty)      fields[OBSERVATION_FIELD_ID['siteDifficulty']] = this.siteDifficulty
    //     if (this.phenology          != this.initialPhenology)           fields[OBSERVATION_FIELD_ID['phenology']] = this.phenology
    //   }
    //   if (this.controlled) {
    //     var treated = this.fullyControlled == 'fully' ? 'Yes' : (this.fullyControlled == 'partially' ? 'Partially' : 'No')
    //     if (treated                 != this.initialTreated)             fields[OBSERVATION_FIELD_ID['treated']] = treated
    //     if (this.dateControlled)      fields[OBSERVATION_FIELD_ID['dateControlled']] = this.dateControlled
    //     if (this.controlMethod      != this.initialControlMethod)       fields[OBSERVATION_FIELD_ID['howTreated']] = this.controlMethod
    //     if (this.treatmentSubstance != this.initialTreatmentSubstance)  fields[OBSERVATION_FIELD_ID['treatmentSubstance']] = this.treatmentSubstance
    //     if (this.treatmentDetails   != this.initialTreatmentDetails)    fields[OBSERVATION_FIELD_ID['treatmentDetails']] = this.treatmentDetails
    //     if (this.dateForNextVisit   != this.initialDateForNextVisit)    fields[OBSERVATION_FIELD_ID['dateForNextVisit']] = this.formattedDateForNextVisit
    //   } else {
    //     if (this.dateOfStatusUpdate)  fields[OBSERVATION_FIELD_ID['dateOfStatusUpdate']] = this.dateOfStatusUpdate
    //     if (this.alive)               fields[OBSERVATION_FIELD_ID['statusUpdate']] = ALIVE_FIELD_VALUE
    //     if (this.dead)                fields[OBSERVATION_FIELD_ID['statusUpdate']] = DEAD_FIELD_VALUE
    //     if (this.duplicate)           fields[OBSERVATION_FIELD_ID['statusUpdate']] = DUPLICATE_FIELD_VALUE
    //   }
    //   
    //   return JSON.stringify(fields)
    // },

    async updateWeedStatus() {
      try {
        // Use provider-specific update endpoint
        const endpoint = this.provider === 'inaturalist' ? '/api/inaturalist/update' : '/api/cams/update'
        const url = new URL(endpoint, window.location.href)
        
        // Use session if available, otherwise fall back to auth-code
        const authParam = this.sessionId ? 'session' : 'auth-code'
        const authValue = this.sessionId || this.code
        
        url.searchParams.set(authParam, authValue)
        url.searchParams.set('state', this.observationId || '1')
        
        var jsonBody = this.createJsonBody()
        console.log(`Updating weed status using ${authParam}:`, url.toString())
        
        var response
        try {
          this.isLoading = true
          this.$emit('update-requested')
          response = await fetch(url, {
              method: "POST",
              headers: {
                'Content-Type': 'application/json'
              },
              body: jsonBody
            }
          )
        } finally {
          this.isLoading = false
        }
        
        const text = await response.text()

        if (!response.ok) {
          // Include detailed error information
          const errorMsg = `Update failed (Status ${response.status}): ${text || response.statusText}`
          throw new Error(errorMsg)
        }
        
        this.message = text  // Use the response from the backend
        this.result = 'success'
      } catch (error) {
        // Show detailed error message to user
        const errorDetails = error.message || error.toString()
        this.message = `Error updating weed status: ${errorDetails}\n\nPlease report this error to systems@econet.nz`
        this.result = 'error'
        console.error('Error updating weed status:', error)
        console.error('Error details:', errorDetails)
      }
    },

    // Initialize duration fields to 0 when first focused
    initializeDurationField(fieldName) {
      if (this[fieldName] === null || this[fieldName] === undefined) {
        this[fieldName] = 0
      }
    },

    // Convert Calendar time input to hours/minutes for This Visit
    updateThisVisitDuration(dateValue) {
      if (dateValue) {
        this.thisVisitHours = dateValue.getHours()
        this.thisVisitMins = dateValue.getMinutes()
      } else {
        this.thisVisitHours = 0
        this.thisVisitMins = 0
      }
    },

    // Convert Calendar time input to hours/minutes for Remaining
    updateRemainingDuration(dateValue) {
      if (dateValue) {
        this.remainingHours = dateValue.getHours()
        this.remainingMins = dateValue.getMinutes()
      } else {
        this.remainingHours = 0
        this.remainingMins = 0
      }
    },

    // Update Calendar when manual input changes for This Visit
    updateThisVisitFromManual() {
      // Only update Calendar if hours are within 24-hour range
      if (this.thisVisitHours < 24) {
        const date = new Date()
        date.setHours(this.thisVisitHours || 0, this.thisVisitMins || 0, 0, 0)
        this.thisVisitDuration = date
      }
    },

    // Update Calendar when manual input changes for Remaining
    updateRemainingFromManual() {
      // Only update Calendar if hours are within 24-hour range
      if (this.remainingHours < 24) {
        const date = new Date()
        date.setHours(this.remainingHours || 0, this.remainingMins || 0, 0, 0)
        this.remainingDuration = date
      }
    },

    // Validate and update This Visit minutes (restrict to 0-60)
    validateAndUpdateThisVisitMins() {
      if (this.thisVisitMins > 60) {
        this.thisVisitMins = 60
      } else if (this.thisVisitMins < 0) {
        this.thisVisitMins = 0
      }
      this.updateThisVisitFromManual()
    },

    // Validate and update Remaining minutes (restrict to 0-60)
    validateAndUpdateRemainingMins() {
      if (this.remainingMins > 60) {
        this.remainingMins = 60
      } else if (this.remainingMins < 0) {
        this.remainingMins = 0
      }
      this.updateRemainingFromManual()
    }
  }
}
</script>

<style scoped>
.test-warning-header {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 2px solid #f59e0b;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(245, 158, 11, 0.1);
}

.production-warning-header {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  border: 2px solid #ef4444;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(239, 68, 68, 0.2);
}

.p-error {
  border: 1px solid red;
}
.p-error span:not(.status-sym) {
  color: red;
  font-size: 0.8rem;
}
.inline-flex {
  display: inline-flex;
  align-items: center;
}

.inline-flex > div {
  margin-right: 1rem;
}

.label-align {
  line-height: 1.5;
}

/* Before/After Comparison Table Styles */
.comparison-table-container {
  overflow-x: auto;
}

.comparison-table {
  border-collapse: collapse;
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.comparison-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.comparison-table td {
  vertical-align: middle;
}

.comparison-table tr:nth-child(even) {
  background: #f8f9fa;
}

.comparison-table tr:hover {
  background: #e9ecef;
}

.comparison-table .p-inputtext {
  min-height: 2.5rem;
}

.comparison-table .p-dropdown {
  min-height: 2.5rem;
}

/* Duration Input Styling */
.duration-input-container {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.duration-input-container:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.duration-hours,
.duration-minutes {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  text-align: center;
  width: 2.5rem;
  padding: 0.25rem 0.125rem;
  font-family: 'Courier New', monospace;
  font-weight: 500;
  font-size: 0.9rem;
}

.duration-hours:focus,
.duration-minutes:focus {
  outline: none;
  background: #f8f9fa !important;
}

.duration-separator {
  color: #6c757d;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  padding: 0 0.125rem;
  user-select: none;
  font-size: 0.9rem;
}

/* Compact Duration Input Styling */
.duration-input-container-compact {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.duration-input-container-compact:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.duration-hours-compact,
.duration-minutes-compact {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  text-align: center;
  width: 2rem;
  padding: 0.375rem 0.125rem;
  font-family: 'Courier New', monospace;
  font-weight: 500;
  font-size: 0.875rem;
}

.duration-hours-compact:focus,
.duration-minutes-compact:focus {
  outline: none;
  background: #f8f9fa !important;
}

.duration-separator-compact {
  color: #6c757d;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  padding: 0 0.0625rem;
  user-select: none;
  font-size: 0.875rem;
}

/* Improved Duration Input Styling */
.duration-input-container-improved {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.duration-input-container-improved:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.duration-hours-improved,
.duration-minutes-improved {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  text-align: center;
  width: 2.75rem;
  padding: 0.5rem 0.25rem;
  font-weight: 400;
  font-size: 1rem;
}

.duration-hours-improved:focus,
.duration-minutes-improved:focus {
  outline: none;
  background: #f8f9fa !important;
}

.duration-separator-improved {
  color: #6c757d;
  font-weight: 600;
  padding: 0 0.125rem;
  user-select: none;
  font-size: 1rem;
}

/* Pods Input Styling */
.pods-input {
  width: 6rem;
  text-align: center;
}

/* Duration Input Hybrid Styles */
.duration-input-hybrid {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.duration-calendar {
  width: 120px;
}

.duration-calendar .p-inputtext {
  text-align: center;
  font-family: monospace;
}

.duration-or {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

.duration-manual {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.duration-separator {
  font-family: monospace;
  font-weight: bold;
  color: #374151;
}

.duration-unit {
  font-size: 0.875rem;
  color: #6b7280;
  margin-left: 0.25rem;
}

/* InputNumber horizontal button styling for compact layout */
.duration-hours-manual .p-inputnumber-button,
.duration-minutes-manual .p-inputnumber-button {
  padding: 0.25rem 0.5rem;
  min-width: auto;
}

.duration-hours-manual,
.duration-minutes-manual {
  display: inline-flex;
  align-items: center;
}

/* Responsive duration input styling */
.duration-input-wrapper {
  width: 6rem;
  min-width: 5.5rem;
  flex-shrink: 0;
}

.duration-input-wrapper .p-inputnumber {
  width: 100% !important;
}

.duration-input-wrapper .p-inputnumber input {
  text-align: center !important;
  padding: 0.375rem 0.5rem !important;
}

.duration-input-wrapper .p-inputnumber-button {
  padding: 0.25rem 0.375rem !important;
  min-width: auto !important;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .duration-input-wrapper {
    width: 6.5rem;
    min-width: 6rem;
  }
  
  .duration-input-wrapper .p-inputnumber input {
    padding: 0.375rem 0.5rem !important;
  }
  
  .duration-input-wrapper .p-inputnumber-button {
    padding: 0.25rem 0.4rem !important;
  }
  
  .duration-unit {
    font-size: 0.75rem;
    margin-left: 0.125rem;
  }
  
  /* Adjust gap between elements on mobile */
  .flex.align-items-center.gap-2 {
    gap: 0.375rem !important;
  }
  
  /* Make labels more compact on mobile */
  .w-6rem {
    width: 4.5rem !important;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .duration-input-wrapper {
    width: 5.5rem;
    min-width: 5rem;
  }
  
  .duration-input-wrapper .p-inputnumber input {
    padding: 0.3rem 0.4rem !important;
    font-size: 0.85rem !important;
  }
  
  .duration-input-wrapper .p-inputnumber-button {
    padding: 0.2rem 0.3rem !important;
    font-size: 0.75rem !important;
  }
  
  .duration-unit {
    font-size: 0.7rem;
  }
  
  .flex.align-items-center.gap-2 {
    gap: 0.25rem !important;
  }
  
  .w-6rem {
    width: 4rem !important;
    font-size: 0.8rem;
  }
}

/* Fieldset content padding */
.fieldset-content {
  padding: 1rem;
}

/* Pods input wrapper styling */
.pods-input-wrapper {
  flex: 1;
}

.pods-input-wrapper .p-inputnumber {
  width: 100% !important;
}

.pods-input-wrapper .p-inputnumber input {
  text-align: center !important;
  padding: 0.75rem 0.5rem !important;
}

.pods-input-wrapper .p-inputnumber-button {
  padding: 0.5rem 0.75rem !important;
}

/* Table input wrapper styling */
.table-input-wrapper {
  width: 100%;
  min-width: 6rem; /* Increased minimum width for decimal values */
}

.table-input-wrapper .p-inputnumber {
  width: 100% !important;
}

.table-input-wrapper .p-inputnumber input {
  text-align: center !important;
  padding: 0.5rem 0.5rem !important; /* More horizontal padding for text */
  min-width: 3rem !important; /* Ensure input field has minimum width */
}

.table-input-wrapper .p-inputnumber-button {
  padding: 0.25rem 0.3rem !important; /* Even narrower spinner buttons */
  min-width: 1.25rem !important; /* More constrained spinner width */
  max-width: 1.25rem !important;
}

/* Update Weed Status button styling */
.update-weed-status-btn {
  background-color: #3b82f6 !important; /* Mid-blue color */
  border-color: #3b82f6 !important;
}

.update-weed-status-btn:hover:not(:disabled) {
  background-color: #2563eb !important; /* Darker blue on hover */
  border-color: #2563eb !important;
}

.update-weed-status-btn:disabled {
  background-color: #9ca3af !important; /* Gray when disabled */
  border-color: #9ca3af !important;
}

/* Font size standardization */
/* Ensure all labels have consistent font size */
.font-medium.text-900 {
  font-size: 0.875rem !important; /* 14px */
}

/* Standardize placeholder text font size */
.p-inputtext::placeholder {
  font-size: 0.875rem !important; /* 14px */
  color: #9ca3af !important;
}

/* Table header font size consistency */
.comparison-table th {
  font-size: 0.875rem !important; /* 14px */
}

/* Ensure table row labels match other labels */
.comparison-table td.font-medium {
  font-size: 0.875rem !important; /* 14px */
}

/* Radio button label consistency */
.flex.align-items-center label {
  font-size: 0.875rem !important; /* 14px */
}

/* Fieldset legend font size */
.p-fieldset .p-fieldset-legend {
  font-size: 1rem !important; /* 16px - slightly larger for section headers */
  font-weight: 600 !important;
}

/* Add small padding between labels and input boxes */
.flex.align-items-center.gap-2 > label + * {
  margin-left: 0.75rem !important; /* 12px padding between label and input */
}

/* Ensure radio button container also gets proper spacing */
.flex.align-items-center.gap-2 > label + .flex {
  margin-left: 0.75rem !important;
}

/* Add padding to error state containers to prevent text touching red border */
.p-error {
  padding: 0.25rem !important; /* 4px padding inside red border */
}

/* Reduce dropdown font size */
.p-select .p-select-label,
.p-select .p-select-option {
  font-size: 0.8125rem !important; /* 13px - slightly smaller than labels */
}

/* Dropdown panel options font size */
.p-select-overlay .p-select-option {
  font-size: 0.8125rem !important;
}

.next-visit-date-field {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.next-visit-date-field .p-datepicker {
  width: auto;
}

.next-visit-date-field .p-datepicker-input {
  width: 8.5rem;
}

.next-visit-help-btn {
  flex-shrink: 0;
}

.next-visit-help {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.4;
}

.next-visit-help p {
  margin: 0 0 0.5rem;
}

.next-visit-help p:last-child {
  margin-bottom: 0;
}

.next-visit-help ul {
  margin: 0;
  padding-left: 1.25rem;
}

.next-visit-help li + li {
  margin-top: 0.35rem;
}

/* Date for next visit label - wider to allow text on two lines instead of four */
.date-next-visit-label {
  width: 5rem !important; /* Increased from 6rem to 7rem - balanced width */
  min-width: 5rem !important;
  line-height: 1.2 !important; /* Allow text to wrap nicely */
}

/* Ensure Update Weed Status button text is white */
.update-weed-status-btn {
  color: white !important;
}

.update-weed-status-btn:hover:not(:disabled) {
  color: white !important;
}

/* Map-aligned status symbols (Option A: Unicode + single color) */
.status-sym {
  display: inline-block;
  font-size: 0.95em;
  line-height: 1;
  vertical-align: middle;
}
/* 1px outline via text-shadow (same pattern as .status-sym-yellow) */
.status-sym-red {
  color: #ff0000;
  text-shadow:
    -1px 0 0 #e60000,
    1px 0 0 #e60000,
    0 -1px 0 #e60000,
    0 1px 0 #e60000,
    -1px -1px 0 #e60000,
    1px -1px 0 #e60000,
    -1px 1px 0 #e60000,
    1px 1px 0 #e60000;
}
.status-sym-purple {
  color: #c500ff;
  text-shadow:
    -1px 0 0 #c500ff,
    1px 0 0 #c500ff,
    0 -1px 0 #c500ff,
    0 1px 0 #c500ff,
    -1px -1px 0 #c500ff,
    1px -1px 0 #c500ff,
    -1px 1px 0 #c500ff,
    1px 1px 0 #c500ff;
}
.status-sym-orange {
  color: #e69800;
  text-shadow:
    -1px 0 0 #e69800,
    1px 0 0 #e69800,
    0 -1px 0 #e69800,
    0 1px 0 #e69800,
    -1px -1px 0 #e69800,
    1px -1px 0 #e69800,
    -1px 1px 0 #e69800,
    1px 1px 0 #e69800;
}
/* Filled shapes with 2px solid border (matches map-style colours; no text-shadow halo) */
.status-sym-dot {
  border-radius: 50%;
  box-sizing: border-box;
  width: 1em;
  height: 1em;
  min-width: 1em;
  min-height: 1em;
  text-shadow: none;
  color: transparent;
  vertical-align: -0.14em;
  line-height: 0;
}
.status-sym-red.status-sym-dot {
  background-color: #ff0000;
  border: 2px solid #e60000;
}
.status-sym-orange.status-sym-dot {
  background-color: #e69800;
  border: 2px solid #e69800;
}
.status-sym-diamond {
  box-sizing: border-box;
  width: 0.78em;
  height: 0.78em;
  border: 2px solid;
  transform: rotate(45deg);
  transform-origin: center;
  vertical-align: middle;
  position: relative;
  top: -0.06em;
  margin: 0 0 0 0.55em;
  text-shadow: none;
  color: transparent;
  line-height: 0;
  flex-shrink: 0;
}
.status-sym-yellow.status-sym-diamond {
  background-color: #ffff00;
  border-color: #bdbd0d;
}
.status-sym-pink.status-sym-diamond {
  background-color: #ffbebe;
  border-color: #ff7f7f;
}
.status-sym-green.status-sym-diamond {
  background-color: #55ff00;
  border-color: #38a800;
}
.status-sym-square {
  border-radius: 0;
  box-sizing: border-box;
  width: 1em;
  height: 1em;
  min-width: 1em;
  min-height: 1em;
  border: 2px solid;
  text-shadow: none;
  color: transparent;
  vertical-align: -0.14em;
  line-height: 0;
}
.status-sym-grey.status-sym-square {
  background-color: #d6d6d6;
  border-color: #999999;
}
.status-sym-black {
  color: #242424;
  text-shadow:
    -1px 0 0 #1a1a1a,
    1px 0 0 #1a1a1a,
    0 -1px 0 #1a1a1a,
    0 1px 0 #1a1a1a,
    -1px -1px 0 #1a1a1a,
    1px -1px 0 #1a1a1a,
    -1px 1px 0 #1a1a1a,
    1px 1px 0 #1a1a1a;
}

</style> 